import { gql } from "graphql-request";
import { z } from "zod";
import { jmApiClient } from "src/jmApiClient";
import { Policy } from "src/gql/graphql";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { app } from "app/Firebase";
import { getTenantId } from "app/tenant";
import actAsUserAtom, { actAsUserIdentityAtom } from "app/uiState/actAsUserAtom";
import { getAuth } from "firebase/auth";
import { getDefaultStore, useAtomValue } from "jotai";
import { queryClient } from "./queryClient";
import { ActAsSessionEndedError } from "./actAsSession";

const currentUserDocument = gql`
  query CurrentUserSession {
    currentUser {
      id
      fullName
      isActive
      deleted
      permissions: allPermissions {
        permission { id }
        policy
      }
      screens: allScreens {
        id
        name
        icon
        group
      }
    }
  }
`;

export const currentUserResponse = z.object({
  currentUser: z.object({
    id: z.string(),
    fullName: z.string().nullable(),
    isActive: z.boolean(),
    deleted: z.boolean(),
    permissions: z.array(z.object({
      permission: z.object({ id: z.string() }),
      policy: z.nativeEnum(Policy),
    })),
    screens: z.array(z.object({
      id: z.string(),
      name: z.string(),
      icon: z.string().nullable(),
      group: z.string().nullable(),
    })),
  }).nullable(),
});

const fetchCurrentUser = async () => {
  // Authentication must read a stable response, not GQty's expiring proxy cache.
  // Invalid/missing status fields reject the query and leave login retryable.
  const response = await jmApiClient.request<unknown>(currentUserDocument);
  return currentUserResponse.parse(response);
};

const currentUserQueryKey = () => [
  "currentUser",
  getTenantId(),
  getAuth(app).currentUser?.uid ?? null,
  getDefaultStore().get(actAsUserAtom)?.actAsUserId ?? null,
];

export const currentUserQueryOptions = (
  actAsUserId = getDefaultStore().get(actAsUserAtom)?.actAsUserId ?? null,
) => {
  const queryKey = currentUserQueryKey();
  queryKey[3] = actAsUserId;
  const assertIdentity = () => {
    const currentKey = currentUserQueryKey();
    if (queryKey.some((value, index) => value !== currentKey[index])) {
      throw new ActAsSessionEndedError();
    }
  };
  return queryOptions({
    queryKey,
    queryFn: async () => {
      // resetQueries can refetch old observers before React renders the new identity.
      assertIdentity();
      const response = await fetchCurrentUser();
      assertIdentity();
      return response;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Share plain session data; authentication explicitly refetches with staleTime: 0.
    gcTime: Infinity,
    staleTime: Infinity,
  });
};

export const resolveCurrentUser = () =>
  queryClient.fetchQuery(currentUserQueryOptions());

export const useCurrentUserData = () => {
  const actAsUserId = useAtomValue(actAsUserIdentityAtom);
  const { data: currentUserData, isFetching } = useQuery(
    currentUserQueryOptions(actAsUserId),
  );

  return { currentUserData, isFetching };
};
